import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Icon from "@/components/icon/Icon";
import { NoPoint, Sizes } from "@/components/icon/IconList";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { cn } from "@/lib/utils";
import { Button, Typography } from "@bookips/sds";
import PassInfoDialog from "@/components/passes/dialogs/PassInfoDialog";
import Image from "next/image";
import { useState } from "react";
import { calculateDiscountedAmount, RadiusBoxStyle } from "..";
import ProductNoticeBox from "./ProductNoticeBox";
import DefaultCashImage from "/public/static/assets/default_cash.png";
import DefaultMembershipImage from "/public/static/assets/default_pass.png";
// 기본 Item row 컴포넌트
const ItemRow = (param)=>{
    let { title , price , originPrice , imageUrl , subTitle , savePoint , isNotAvailablePoint  } = param;
    const discountedAmount = calculateDiscountedAmount({
        price: originPrice,
        discount: price
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-start gap-5",
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "w-[68px] h-[68px] relative rounded-md border border-gray-300/20 overflow-hidden shrink-0",
                children: /*#__PURE__*/ _jsx(Image, {
                    src: imageUrl || DefaultMembershipImage,
                    alt: "library-detail-bg",
                    fill: true
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-3",
                children: [
                    /*#__PURE__*/ _jsxs(Typography, {
                        variant: "body1",
                        color: "text-strong",
                        className: "flex flex-col gap-1",
                        children: [
                            title,
                            subTitle && /*#__PURE__*/ _jsx(Typography, {
                                variant: "body2",
                                color: "text-muted",
                                children: subTitle
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-col gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs(Typography, {
                                variant: "subtitle1",
                                className: " text-strong flex gap-x-1.5 items-center",
                                children: [
                                    price.toLocaleString(),
                                    "원",
                                    parseInt(discountedAmount) > 0 && /*#__PURE__*/ _jsxs(Typography, {
                                        variant: "caption",
                                        className: "line-through text-muted",
                                        children: [
                                            originPrice.toLocaleString(),
                                            "원"
                                        ]
                                    })
                                ]
                            }),
                            savePoint > 0 && /*#__PURE__*/ _jsxs(Typography, {
                                variant: "body2",
                                className: "text-status-data",
                                children: [
                                    "적립 포인트",
                                    /*#__PURE__*/ _jsxs(Typography, {
                                        variant: "subtitle2",
                                        className: "text-status-data",
                                        children: [
                                            " +".concat(savePoint.toLocaleString()),
                                            "원"
                                        ]
                                    })
                                ]
                            }),
                            isNotAvailablePoint && /*#__PURE__*/ _jsxs(Typography, {
                                variant: "caption",
                                className: "text-secondary flex items-center gap-1",
                                children: [
                                    /*#__PURE__*/ _jsx(Icon, {
                                        icon: NoPoint,
                                        size: Sizes.small,
                                        className: "text-muted w-4 h-4"
                                    }),
                                    "포인트 사용 불가 상품"
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
// 이용권 아이템 row 레이아웃
export const MembershipItemRow = (param)=>{
    let { data  } = param;
    const [isPassInfoOpen, setIsPassInfoOpen] = useState(false);
    return /*#__PURE__*/ _jsxs(RadiusBoxStyle, {
        className: cn("flex flex-col gap-6", "max-sm:rounded-none max-sm:border-none max-sm:p-5"),
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "h6",
                className: "flex items-center gap-2",
                children: "주문상품"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-5",
                children: [
                    /*#__PURE__*/ _jsx(ItemRow, {
                        title: data.title,
                        price: data.price,
                        originPrice: data.originPrice,
                        isNotAvailablePoint: data.isPointUnusable
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        variant: "stroke",
                        color: "secondary",
                        className: "w-full text-primary",
                        size: "lg",
                        onClick: ()=>{
                            mixpanelEvent("Product Detail Button Clicked", {
                                "Product Categories": "pass",
                                "Product ID": data.id,
                                "Product Title": data.title,
                                "Product Price": data.price,
                                "Button UI": "Arrow"
                            });
                            setIsPassInfoOpen(true);
                        },
                        children: "이용권 정보 보기"
                    })
                ]
            }),
            isPassInfoOpen && /*#__PURE__*/ _jsx(PassInfoDialog, {
                isOpen: true,
                pass: data,
                mode: "purchase",
                position: "주문결제",
                primaryLabel: "확인",
                onPrimary: ()=>setIsPassInfoOpen(false),
                onClose: ()=>setIsPassInfoOpen(false)
            })
        ]
    });
};
// 패키지 아이템 row 레이아웃
export const PackageItemRow = (param)=>{
    let { data  } = param;
    return /*#__PURE__*/ _jsxs(RadiusBoxStyle, {
        className: cn("flex flex-col gap-6", "max-sm:rounded-none max-sm:border-none max-sm:p-5"),
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "h6",
                className: "flex items-center gap-2",
                children: "주문상품"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-5",
                children: [
                    /*#__PURE__*/ _jsx(ItemRow, {
                        title: data.title,
                        price: data.price,
                        originPrice: data.originalPrice,
                        imageUrl: data.imageUrl,
                        isNotAvailablePoint: data.isPointUnusable
                    }),
                    /*#__PURE__*/ _jsx(ProductNoticeBox, {
                        description: data.description
                    })
                ]
            })
        ]
    });
};
export const CashItemRow = (param)=>{
    let { data , savePoint  } = param;
    var _data_products;
    const cashProduct = (_data_products = data.products) === null || _data_products === void 0 ? void 0 : _data_products[0];
    if (!cashProduct) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(RadiusBoxStyle, {
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "h6",
                className: "flex items-center gap-2",
                children: "주문상품"
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex flex-col gap-5",
                children: /*#__PURE__*/ _jsx(ItemRow, {
                    title: "쏠북캐시 충전",
                    price: cashProduct.price,
                    imageUrl: DefaultCashImage.src,
                    savePoint: savePoint,
                    isNotAvailablePoint: null
                })
            })
        ]
    });
};
